import type {ReactElement} from 'react';
import React from 'react';
import {NavLogo} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-logo';
import {NavBackButton} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-back-button';
import {NavUndoRedoOptions} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-undo-redo-options';
import {Text, TextSize} from '@Components/text';
import {ElementToHide} from '@Components/poster-editor/poster-editor.types';
import {NavFileDropdown} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-file-dropdown';
import {LogoDisplayOptions} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-left-options/nav-left-options.types';
import {closeEmbeddedEditor} from '@Libraries/embedded-editor';
import {closeVersionHistoryPanel, isEditorMobileVariant} from '@/components/poster-editor/library/poster-editor-library';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {NavResizeOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-resize-option/nav-resize-option';

export interface NavLeftOptionsProps {
  className?: string;
  showBackButton?: boolean;
  showLogo?: LogoDisplayOptions;
  showResize?: boolean;
  showFileDropdown?: boolean;
  isEmbedded?: boolean;
  hasSmallLogoMargin?: boolean;
  isVersionHistory?: boolean;
}

export function NavLeftOptions({
  className = '',
  showBackButton = true,
  showResize = true,
  showLogo = LogoDisplayOptions.SHORT,
  isEmbedded = false,
  showFileDropdown = true,
  hasSmallLogoMargin = false,
  isVersionHistory = false,
}: NavLeftOptionsProps): ReactElement<any> | null {
  const getBackButton = (): ReactElement<any> | null => {
    if (isVersionHistory && !isEditorMobileVariant()) {
      return <NavBackButton onClick={onVersionHistoryNavBack} />;
    }

    if (showBackButton) {
      return <NavBackButton onClick={onNavBack} />;
    }

    return null;
  };

  const onNavBack = (): void => {
    if (isEmbedded) {
      onEmbeddedNavBack();
      return;
    }

    onNonEmbeddedNavBack();
  };

  const onEmbeddedNavBack = (): void => {
    closeEmbeddedEditor();
  };

  const onNonEmbeddedNavBack = (): void => {
    const {history} = window;
    if (history.length > 2) {
      window.history.back();
    } else {
      window.location = window.PMW.util.homepage_url();
    }
  };

  const onVersionHistoryNavBack = (): void => {
    closeVersionHistoryPanel();
  };

  const getLogo = (): ReactElement<any> | null => {
    if (isVersionHistory && isEditorMobileVariant()) {
      return <NavLogo hasSmallLogoMargin={hasSmallLogoMargin} showFullLogo={false} />;
    }

    if (isVersionHistory) {
      return null;
    }

    if (isEmbedded) {
      return null;
    }
    switch (showLogo) {
      case LogoDisplayOptions.HIDDEN:
        return null;
      case LogoDisplayOptions.SHORT:
        return <NavLogo hasSmallLogoMargin={hasSmallLogoMargin} showFullLogo={false} />;
      case LogoDisplayOptions.FULL:
        return <NavLogo showFullLogo />;
      default:
        throw new Error('unhandled logo display option');
    }
  };

  const getFileDropdownSelector = (): ReactElement<any> | null => {
    if (isVersionHistory) {
      return null;
    }

    if (window.posterEditor?.config?.elementsToHide?.includes(ElementToHide.FILE)) {
      return null;
    }

    if (showFileDropdown) {
      return <NavFileDropdown />;
    }
    return null;
  };

  const getUndoRedoOptions = (): ReactElement<any> | null => {
    if (isVersionHistory) {
      return null;
    }

    return <NavUndoRedoOptions />;
  };

  const getEditDesignText = (): ReactElement<any> | null => {
    if (isVersionHistory) {
      return null;
    }

    if (!isEmbedded) {
      return null;
    }

    return <Text size={TextSize.SMALL} className="spacing-m-r-4 spacing-m-l-2" val={window.i18next.t('pmwjs_edit_design_capitalized')} bold />;
  };

  const getVersionHistoryText = (): ReactElement<any> | null => {
    if (!isVersionHistory || isEditorMobileVariant()) {
      return null;
    }

    return <Text size={TextSize.SMALL} className="spacing-m-r-4 spacing-m-l-2" val={window.i18next.t('pmwjs_version_history')} bold />;
  };

  const showResizeOption = (): ReactElement<any> | null => {
    if (isVersionHistory || isEditorMobileVariant() || !showResize) {
      return null;
    }

    return <NavResizeOption resizeOptionType={NavRightSideOptionTypes.BUTTON} />;
  };

  return (
    <div className={`js-blurred-during-onboarding flex-row-align-center ${className}`}>
      {getBackButton()}
      {getVersionHistoryText()}
      {getEditDesignText()}
      {getLogo()}
      {getFileDropdownSelector()}
      {showResizeOption()}
      {getUndoRedoOptions()}
    </div>
  );
}
